import { checkRecommend } from './scripts';

const DESKTOP_BREAKPOINT = 768;

const PAINT_CATEGORY_NAMES = [
  'paints',
  'paint',
  'farben',
  'peintures',
  'pinturas',
  'pitture e vernici',
];

function initCartDrawer() {
  return {
    open: false,
    firstLoad: true, // Lock to avoid the cart preview to open after every page load
    cart: {},
    isLoading: false,
    customerGroupId: false,
    cartSummaryCount: 0,
    cartItems: [],
    customer: {},
    isExpandedDrawer: false,
    isInterruptorMounted: false,
    isMountRetryPending: false,

    isDesktop() {
      return window.innerWidth >= DESKTOP_BREAKPOINT;
    },

    // Check if interruptor drawer should be shown on desktop
    // Only checks PHP flag (enabled + logged in + customer group)
    // Note: We don't check mbSkipAssistant cookie here because:
    // 1. We set it ourselves when showing drawer (to skip redirect on checkout)
    // 2. The cookie would incorrectly block drawer on subsequent opens
    canShowInterruptorDrawer() {
      // Check PHP-side conditions (enabled, logged in, customer group allowed)
      return window.mb?.cartDrawer?.canShowInterruptorDrawer === true;
    },

    mountInterruptor() {
      if (this.isInterruptorMounted) return;

      const container = document.getElementById('interruptor-drawer-root');
      if (!container) return;

      // Wait for interruptor.js to be loaded
      if (typeof window.mountInterruptorDrawer === 'function') {
        window.mountInterruptorDrawer(container, 'drawer');
        this.isInterruptorMounted = true;
        this.isMountRetryPending = false;
      } else if (!this.isMountRetryPending) {
        this.isMountRetryPending = true;
        setTimeout(() => {
          this.isMountRetryPending = false;
          this.mountInterruptor();
        }, 100);
      }
    },

    unmountInterruptor() {
      if (typeof window.unmountInterruptorDrawer === 'function') {
        window.unmountInterruptorDrawer();
      }
      this.isInterruptorMounted = false;
    },

    getData(data) {
      if (data.cart) {
        this.cart = data.cart;
        this.setCartItems();

        this.cartSummaryCount = this.cart.items?.length;
        this.firstLoad = false;
      }
      if (data.customer) {
        this.customer = data.customer;
        if (data.customer.groupId) {
          this.customerGroupId = data.customer.groupId;
        }
      }

      // Mount interruptor early so /recommend fetches in background
      if (
        this.isDesktop() &&
        this.canShowInterruptorDrawer() &&
        this.hasNonPaintItems()
      ) {
        this.mountInterruptor();
      }
    },
    setCartItems() {
      // Prevent the cart to be sorted externally
      this.cartItems = this.cart
        ? [...this.cart.items].sort((a, b) => a.item_id - b.item_id)
        : [];
    },
    blockCartDrawer() {
      this.firstLoad = true;
    },
    hasNonPaintItems() {
      return (
        this.cart?.items?.some((item) => {
          const taxonomy = item.taxonomy_us || '';
          const category = taxonomy.split(/[.|>]/)?.[1]?.toLowerCase() || '';

          return category ? !PAINT_CATEGORY_NAMES.includes(category) : true;
        }) ?? false
      );
    },

    openCart() {
      this.open = true;
      document.body.classList.add('cart-drawer-open');

      if (
        this.isDesktop() &&
        this.canShowInterruptorDrawer() &&
        this.hasNonPaintItems()
      ) {
        document.cookie = 'mbSkipAssistant=1; path=/';
        this.isExpandedDrawer = true;
        this.$nextTick(() => {
          this.mountInterruptor();
        });
      } else {
        this.isExpandedDrawer = false;
        checkRecommend(
          JSON.parse(JSON.stringify(this.cart.items)),
          this.customer,
        );
      }

      const intercom = document.querySelector('.intercom-launcher');
      if (intercom) {
        intercom.classList.add('hidden');
      }
    },
    closeCart() {
      this.open = false;
      document.body.classList.remove('cart-drawer-open');

      // Delay collapsing expanded drawer until slide-out animation finishes (700ms)
      // so interruptor and cart slide out together as one unit
      if (this.isExpandedDrawer) {
        setTimeout(() => {
          this.isExpandedDrawer = false;
        }, 700);
      }

      const intercom = document.querySelector('.intercom-launcher');
      if (intercom) {
        intercom.classList.remove('block');
      }
    },
    getProductCategory(item) {
      const product = item?.product || item;
      const taxonomies =
        product?.taxonomy_us?.split('|')?.[0]?.split('.') || [];
      let category = taxonomies[1] || '';

      if (!category && product?.category_names) {
        category = product.category_names.split(',')[0];
      }

      return category;
    },
    startLoading() {
      this.isLoading = true;
      document.body.classList.add('overflow-hidden');
      window.dispatchEvent(
        new CustomEvent('start-loader', { detail: this.isLoading }),
      );
    },
    stopLoading() {
      this.isLoading = false;
      document.body.classList.remove('overflow-hidden');
      window.dispatchEvent(
        new CustomEvent('start-loader', { detail: this.isLoading }),
      );
    },
    increaseItemQty(item) {
      const newQty = parseInt(item.qty) + 1;
      this.mutateItemQty(item, newQty);
    },
    decreaseItemQty(item) {
      if (item.qty <= 1) {
        this.mutateItemRemove(item.item_id);
        return;
      }
      const newQty = parseInt(item.qty) - 1;
      this.mutateItemQty(item, newQty);
    },
    inputMaxMinQtyValue(item, qtyInput) {
      const maxValue = parseInt(qtyInput.max);
      const minValue = parseInt(qtyInput.min);

      if (item.qty > maxValue) {
        item.qty = maxValue;
      } else if (item.qty < minValue) {
        item.qty = minValue;
      }
      this.mutateItemQty(item, item.qty);
    },
    mutateItemQty(item, qty) {
      this.startLoading();

      const updateItemQtyUrl = window.mb.cartDrawer.updateItemQtyUrl;
      const formData = new FormData();
      const itemId = item.item_id;
      formData.append('item_id', itemId);
      formData.append('qty', qty);

      fetch(updateItemQtyUrl, {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error(window.mb.cartDrawer.wrongMessage);
        })
        .then((data) => {
          if (data?.status === 'failed') {
            dispatchMessages(
              [
                {
                  type: 'error',
                  text: data?.message || window.mb.cartDrawer.errorMessage,
                },
              ],
              5000,
            );
          } else {
            this.analyticsTrackCartItemChangeQty(itemId, qty);

            this.cart.items.some((cartItem) => {
              if (cartItem.item_id === itemId) {
                cartItem.qty = qty;
                return true;
              }
              return false;
            });
            this.reloadCustomerData();
          }
        })
        .finally(() => {
          this.stopLoading();
        });
    },
    mutateItemRemove(itemId) {
      this.startLoading();
      const removeItemUrl = window.mb.cartDrawer.removeItemUrl;
      const formData = new FormData();
      formData.append('item_id', itemId);

      fetch(removeItemUrl, {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error(window.mb.cartDrawer.wrongMessage);
        })
        .then((data) => {
          if (data?.status === 'failed') {
            dispatchMessages(
              [
                {
                  type: 'error',
                  text: data?.message || window.mb.cartDrawer.errorMessage,
                },
              ],
              5000,
            );
            return;
          }

          this.cartItems = this.cartItems.filter(
            (item) => item.item_id !== itemId,
          );
          this.cart.items = this.cart.items.filter(
            (item) => item.item_id !== itemId,
          );
          this.cartSummaryCount = this.cart.items?.length || 0;

          // Collapse expanded drawer when cart becomes empty or only paints remain
          if (
            this.isExpandedDrawer &&
            (this.cartSummaryCount === 0 || !this.hasNonPaintItems())
          ) {
            this.unmountInterruptor();
            this.isExpandedDrawer = false;
          } else if (this.cartSummaryCount > 0 && !this.isExpandedDrawer) {
            // Mobile: check recommendations
            checkRecommend(
              JSON.parse(JSON.stringify(this.cart.items)),
              this.customer,
            );
          }
          this.analyticsTrackCartItemChangeQty(itemId, 0);
          this.reloadCustomerData();
        })
        .finally(() => {
          this.stopLoading();
        });
    },
    reloadCustomerData() {
      hyva.setCookie('mage-cache-sessid', '', -1, true); // remove the cookie to force customer section
      const reloadCustomerDataEvent = new CustomEvent(
        'reload-customer-section-data',
      );
      window.dispatchEvent(reloadCustomerDataEvent);
    },
    analyticsTrackCartItemChangeQty(itemId, newQty) {
      const item = this.cart?.items?.find((item) => item.item_id === itemId);
      if (!item) {
        return;
      }

      const isIncrease = newQty > (item.qty || 0);
      const eventName = isIncrease ? 'add_to_cart' : 'remove_from_cart';
      const formattedItem = item && this.prepareGTMItemData(item, eventName);
      formattedItem.quantity = Math.abs(item.qty - newQty);

      mitGTM.sendEvent({ ecommerce: null });

      const event = {
        event: eventName,
        list_id: null,
        list_name: null,
        event_location: 'Item in mini cart',
        method: 'Single',
        ecommerce: {
          currency: ENVIRONMENT_REGION === 'US' ? 'USD' : 'EUR',
          value: 0, // All MB items are $0
          items: [formattedItem],
        },
      };
      mitGTM.sendEvent(event);
    },
    prepareGTMItemData(item) {
      const product = item?.product || item;
      const taxonomies = product?.taxonomy_us
        ? mitGTM.sanitizeTaxonomy(product.taxonomy_us)
        : {};
      const itemData = {
        item_name: product?.product_name || null,
        item_id: product?.product_sku || null,
        item_brand:
          product?.manufacturer_label || product?.options?.manufacturer || null,
        item_type: mitGTM.getItemType(product),
        item_category: Array.isArray(taxonomies)
          ? taxonomies[0]
          : taxonomies?.category || null,
        item_category2: Array.isArray(taxonomies)
          ? taxonomies[1]
          : taxonomies?.category2 || null,
        item_category3: Array.isArray(taxonomies)
          ? taxonomies[2]
          : taxonomies?.category3 || null,
        item_variant:
          product?.color?.[0] ||
          product.color ||
          product?.options?.color ||
          null,
        quantity: item.qty,
        price: 0,
        item_list_id:
          item?.item_list_id || product?.options?.item_list_id || null,
        item_list_name:
          item?.item_list_name || product?.options?.item_list_name || null,
        index: item?.index || product?.options?.index || null,
      };

      if (item?.related_product) {
        itemData['associated_item'] = item.related_product;
      }

      return itemData;
    },
  };
}

window.initCartDrawer = initCartDrawer;
